import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import BasicLayout from '../components/Shared/BasicLayout';
import Constants from '../constants';
import { Navigate } from '../react-router';
import { UserContext, WpUserAction, Access } from '../contexts/user';
import { useMutation } from '@apollo/client';
import { UPDATE_ORGANIZATION } from '../graphql/user';
import Input, { Label, Info } from '../components/Input';
import Checkbox from '../components/Checkbox';
import { Button } from '@ologyhealth/wp-components';
import Toast from 'react-native-toast-message';
import Spinner from '../components/Spinner';

export default function Ai(): JSX.Element {
  const { state, dispatch } = React.useContext(UserContext);

  const [aiEnabled, setAiEnabled] = useState<boolean>(false);
  const [aiPrompt, setAiPrompt] = useState<string>('');
  const [aiContact, setAiContact] = useState<string>('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  const [updateOrganization, updateOrganizationData] = useMutation(UPDATE_ORGANIZATION, {
    fetchPolicy: "no-cache",
  });

  // Load initial values from organization
  useEffect(() => {
    if (state.user?.organization) {
      setAiEnabled(state.user.organization.aiEnabled || false);
      setAiPrompt(state.user.organization.aiPrompt || '');
      setAiContact(state.user.organization.aiContact || '');
    }
  }, [state.user?.organization]);

  // Handle update response
  useEffect(() => {
    if (updateOrganizationData.data?.updateOrganization) {
      const org = updateOrganizationData.data.updateOrganization.organization;
      if (updateOrganizationData.data.updateOrganization.success) {
        setAiEnabled(org.aiEnabled || false);
        setAiPrompt(org.aiPrompt || '');
        setAiContact(org.aiContact || '');
        dispatch({ type: WpUserAction.REFRESH_ORGANIZATION, organization: org });
        Toast.show({
          type: 'success',
          text1: 'Saved',
          text2: 'Your AI settings have been saved.'
        });
        setError(undefined);
      } else {
        if (updateOrganizationData.data.updateOrganization.errors && updateOrganizationData.data.updateOrganization.errors.length > 0) {
          setError(updateOrganizationData.data.updateOrganization.errors[0]);
        }
      }
      setLoading(false);
    }
  }, [updateOrganizationData.data, dispatch]);

  const onSave = () => {
    if (!state.user?.organization) {
      return;
    }

    // Validation: require aiPrompt and aiContact if aiEnabled is true
    if (aiEnabled && !aiPrompt.trim()) {
      setError('AI instructions are required when AI is enabled.');
      return;
    }

    if (aiEnabled && !aiContact.trim()) {
      setError('Contact information is required when AI is enabled.');
      return;
    }

    // Character limits validation
    if (aiPrompt && aiPrompt.length > 5000) {
      setError('AI prompt must be 5000 characters or less.');
      return;
    }

    if (aiContact && aiContact.length > 200) {
      setError('Contact information must be 200 characters or less.');
      return;
    }

    setError(undefined);
    setLoading(true);

    if ([Access.ADMIN, Access.MANAGER, Access.OWNER].indexOf(state.user.organization.access) > -1) {
      updateOrganization({
        variables: {
          organizationId: state.user.organization.id,
          organization: {
            aiEnabled: aiEnabled,
            aiPrompt: aiPrompt.trim() || null,
            aiContact: aiContact.trim() || null,
          }
        }
      });
    } else {
      setError('You do not have permission to update these settings.');
      setLoading(false);
    }
  };

  const defaultPrompt = `You are a helpful AI assistant for ${Constants.brand}. Your role is to:
- Respond to customer inquiries professionally and accurately
- Provide information about services and appointments
- Escalate complex issues to human support when needed
- Maintain a friendly and professional tone
- Always prioritize customer satisfaction

When you cannot help with a request, politely direct the customer to contact support using the provided contact information.`;

  if (!state.user) {
    return (
      <BasicLayout active="/ai">
        <View style={styles.container}>
          <Text style={styles.heading}>AI Settings</Text>
          <Text style={styles.subtitle}>Please log in to access AI settings.</Text>
        </View>
      </BasicLayout>
    );
  }

  return (
    <BasicLayout active="/ai">
      <View style={styles.container}>
        <Text style={styles.heading}>AI Settings</Text>
        <Text style={styles.subtitle}>Configure your AI agent and auto-text settings</Text>

        <View style={styles.settingsContainer}>
          {/* Enable AI Toggle */}
          <View style={styles.settingRow}>
            <View style={styles.settingLabelContainer}>
              <Checkbox
                selected={aiEnabled}
                onSelect={(val) => {
                  if (state.user?.organization && [Access.ADMIN, Access.MANAGER, Access.OWNER].indexOf(state.user.organization.access) > -1) {
                    setAiEnabled(val);
                  }
                }}
                color={Constants.colors.info}
                colorActive={Constants.colors.blue}
                hoverable={state.user?.organization && [Access.ADMIN, Access.MANAGER, Access.OWNER].indexOf(state.user.organization.access) > -1}
              />
              <Text style={styles.settingLabel}>Enable AI Responses</Text>
            </View>
            <Info info="When enabled, the AI will automatically respond to incoming messages based on your instructions." />
          </View>

          {/* AI Prompt/Instructions */}
          <View style={styles.settingRow}>
            <Label name="AI Instructions/Prompt" optional={!aiEnabled ? 'Optional' : 'Required when AI is enabled'} />
            <Input
              name=""
              value={aiPrompt}
              setValue={setAiPrompt}
              multiline={true}
              placeholder={defaultPrompt}
              style={styles.textArea}
              disabled={!state.user?.organization || [Access.ADMIN, Access.MANAGER, Access.OWNER].indexOf(state.user.organization.access) === -1}
            />
            <Info info="Provide detailed instructions for how the AI should respond. This will guide the AI's behavior when handling customer inquiries. Required when AI is enabled." />
            {aiPrompt && (
              <Text style={styles.charCount}>
                {aiPrompt.length} / 5000 characters
              </Text>
            )}
          </View>

          {/* Contact Info for Escalation */}
          <View style={styles.settingRow}>
            <Label name="Contact Info for Escalation" optional={!aiEnabled ? "Optional" : "Required when AI is enabled"} />
            <Input
              name=""
              value={aiContact}
              setValue={setAiContact}
              placeholder="e.g., support@example.com or +1 (555) 123-4567"
              style={styles.textInput}
              disabled={!state.user?.organization || [Access.ADMIN, Access.MANAGER, Access.OWNER].indexOf(state.user.organization.access) === -1}
            />
            <Info info="Phone number or email address where customers should be directed when the AI cannot help. This is required when AI is enabled." />
            {aiContact && (
              <Text style={styles.charCount}>
                {aiContact.length} / 200 characters
              </Text>
            )}
          </View>

          {/* Error Message */}
          {error && (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          {/* Save Button - Only show for users with permission to update */}
          {state.user?.organization && [Access.ADMIN, Access.MANAGER, Access.OWNER].indexOf(state.user.organization.access) > -1 && (
            <View style={styles.buttonContainer}>
              <Button
                type="Primary"
                size="Default"
                shape="Pill"
                onClick={onSave}
                disabled={loading || updateOrganizationData.loading}
              >
                {loading || updateOrganizationData.loading ? (
                  <Spinner color="#FFF" />
                ) : (
                  <Text style={{ color: '#FFF', fontWeight: 'bold' }}>Save Settings</Text>
                )}
              </Button>
            </View>
          )}
          
          {/* Info message for users without edit permission */}
          {state.user?.organization && [Access.ADMIN, Access.MANAGER, Access.OWNER].indexOf(state.user.organization.access) === -1 && (
            <View style={styles.infoContainer}>
              <Text style={styles.infoText}>
                You can view the AI settings, but only organization administrators can make changes.
              </Text>
            </View>
          )}
        </View>
      </View>
    </BasicLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 25,
    maxWidth: 800,
    width: '100%'
  },
  heading: {
    fontSize: 32,
    fontFamily: 'GothamBold',
    color: Constants.colors.blue,
    marginBottom: 10
  },
  subtitle: {
    fontSize: 16,
    fontFamily: 'Gotham',
    color: Constants.colors.info,
    marginBottom: 30
  },
  settingsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 30
  },
  settingRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  settingLabelContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5
  },
  settingLabel: {
    fontSize: 18,
    fontFamily: 'GothamBold',
    color: Constants.colors.darkBlue
  },
  textArea: {
    minHeight: 200,
    textAlign: 'left',
    padding: 15,
    fontSize: 14,
    fontFamily: 'Gotham'
  },
  textInput: {
    textAlign: 'left',
    padding: 15,
    fontSize: 14,
    fontFamily: 'Gotham'
  },
  charCount: {
    fontSize: 12,
    fontFamily: 'Gotham',
    color: Constants.colors.info,
    alignSelf: 'flex-end'
  },
  errorContainer: {
    backgroundColor: '#FEE',
    borderColor: Constants.colors.red,
    borderWidth: 1,
    borderRadius: 5,
    padding: 15,
    marginTop: 10
  },
  errorText: {
    color: Constants.colors.red,
    fontFamily: 'Gotham',
    fontSize: 14
  },
  buttonContainer: {
    marginTop: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  infoContainer: {
    backgroundColor: '#F0F8FF',
    borderColor: Constants.colors.blue,
    borderWidth: 1,
    borderRadius: 5,
    padding: 15,
    marginTop: 20
  },
  infoText: {
    color: Constants.colors.darkBlue,
    fontFamily: 'Gotham',
    fontSize: 14
  }
});




